import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GraduationCap, Upload, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';

interface University {
  id: string;
  name_en: string;
  name_ar: string;
  city: string;
  lat: number;
  lon: number;
  type?: string;
  academic_terms: any;
  common_zones: any;
  created_at: string;
}

interface UniversityFormData {
  name_en: string;
  name_ar: string;
  city: string;
}

const UniversitiesManagement = () => {
  const { t, language } = useLanguage();
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState<University | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<UniversityFormData>({
    name_en: '',
    name_ar: '',
    city: ''
  });

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      console.log('Fetching universities...');
      const { data, error } = await supabase
        .from('universities')
        .select('*')
        .order('name_en');

      if (error) {
        console.error('Error fetching universities:', error);
        throw error;
      }
      
      console.log('Fetched universities:', data);
      setUniversities(data || []);
    } catch (error) {
      console.error('Error fetching universities:', error);
      toast.error('Failed to fetch universities');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUniversities();
    setRefreshing(false);
    toast.success('Universities list refreshed');
  };

  const resetForm = () => {
    setFormData({
      name_en: '',
      name_ar: '',
      city: ''
    });
  };

  const handleAdd = async () => {
    try {
      const { error } = await supabase
        .from('universities')
        .insert([{
          name_en: formData.name_en,
          name_ar: formData.name_ar,
          city: formData.city,
          lat: 0, // Default coordinates
          lon: 0,
          academic_terms: {},
          common_zones: {}
        }]);

      if (error) throw error;
      
      toast.success('University added successfully');
      setShowAddDialog(false);
      resetForm();
      await fetchUniversities(); // Refresh the list
    } catch (error) {
      console.error('Error adding university:', error);
      toast.error('Failed to add university');
    }
  };

  const handleEdit = (university: University) => {
    setEditingUniversity(university);
    setFormData({
      name_en: university.name_en,
      name_ar: university.name_ar,
      city: university.city
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editingUniversity) return;

    try {
      const { error } = await supabase
        .from('universities')
        .update({
          name_en: formData.name_en,
          name_ar: formData.name_ar,
          city: formData.city
        })
        .eq('id', editingUniversity.id);

      if (error) throw error;
      
      toast.success('University updated successfully');
      setShowEditDialog(false);
      setEditingUniversity(null);
      resetForm();
      await fetchUniversities(); // Refresh the list
    } catch (error) {
      console.error('Error updating university:', error);
      toast.error('Failed to update university');
    }
  };

  const handleDelete = async (universityId: string, universityName: string) => {
    if (!confirm(`Are you sure you want to delete ${universityName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('universities')
        .delete()
        .eq('id', universityId);

      if (error) throw error;
      toast.success('University deleted successfully');
      await fetchUniversities(); // Refresh the list
    } catch (error) {
      console.error('Error deleting university:', error);
      toast.error('Failed to delete university');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Read the file and convert to text for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const csv = XLSX.utils.sheet_to_csv(worksheet);
          
          // Remove the header row and set as preview text
          const lines = csv.split('\n');
          const dataLines = lines.slice(1).filter(line => line.trim());
          setBulkImportText(dataLines.join('\n'));
        } catch (error) {
          console.error('Error reading Excel file:', error);
          toast.error('Error reading Excel file. Please check the file format.');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleBulkImport = async () => {
    let universitiesToAdd = [];

    try {
      if (selectedFile) {
        // Process Excel file
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            // Skip header row and process data
            const dataRows = jsonData.slice(1) as any[][];
            for (const row of dataRows) {
              if (row.length >= 3 && row[0] && row[1] && row[2]) {
                universitiesToAdd.push({
                  name_en: String(row[0]).trim(),
                  name_ar: String(row[1]).trim(),
                  city: String(row[2]).trim(),
                  lat: 0,
                  lon: 0,
                  academic_terms: {},
                  common_zones: {}
                });
              }
            }
            await processImport(universitiesToAdd);
          } catch (error) {
            console.error('Error processing Excel file:', error);
            toast.error('Failed to process Excel file');
          }
        };
        reader.readAsArrayBuffer(selectedFile);
      } else if (bulkImportText.trim()) {
        // Process text input
        const lines = bulkImportText.trim().split('\n');
        for (const line of lines) {
          const parts = line.split(',').map(part => part.trim());
          if (parts.length >= 3) {
            universitiesToAdd.push({
              name_en: parts[0],
              name_ar: parts[1],
              city: parts[2],
              lat: 0,
              lon: 0,
              academic_terms: {},
              common_zones: {}
            });
          }
        }
        await processImport(universitiesToAdd);
      } else {
        toast.error('Please upload an Excel file or enter universities data');
      }
    } catch (error) {
      console.error('Error during bulk import:', error);
      toast.error('Failed to import universities');
    }
  };

  const processImport = async (universitiesToAdd: any[]) => {
    if (universitiesToAdd.length === 0) {
      toast.error('No valid university data found. Please check the format.');
      return;
    }

    try {
      console.log('Attempting to import universities:', universitiesToAdd);
      
      // Check current user's auth status
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('Current user:', user);
      
      if (authError) {
        console.error('Auth error:', authError);
        toast.error('Authentication error. Please log in again.');
        return;
      }

      if (!user) {
        toast.error('You must be logged in to import universities.');
        return;
      }

      const { data, error } = await supabase
        .from('universities')
        .insert(universitiesToAdd)
        .select();

      if (error) {
        console.error('Import error details:', error);
        throw error;
      }
      
      console.log('Successfully imported:', data);
      toast.success(`Successfully imported ${universitiesToAdd.length} universities`);
      setShowBulkImportDialog(false);
      setBulkImportText('');
      setSelectedFile(null);
      
      // Refresh the list to show new universities
      await fetchUniversities();
    } catch (error) {
      console.error('Error importing universities:', error);
      if (error.message?.includes('permission')) {
        toast.error('Permission denied. You need admin privileges to import universities.');
      } else {
        toast.error(`Failed to import universities: ${error.message}`);
      }
    }
  };

  const downloadTemplate = () => {
    const template = `English Name,Arabic Name,City
Cairo University,جامعة القاهرة,Cairo
American University in Cairo,الجامعة الأمريكية بالقاهرة,Cairo
German University in Cairo,الجامعة الألمانية بالقاهرة,Cairo`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'universities_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadExcelTemplate = () => {
    // Create sample data for Excel template
    const sampleData = [
      ['English Name', 'Arabic Name', 'City'],
      ['Cairo University', 'جامعة القاهرة', 'Cairo'],
      ['American University in Cairo', 'الجامعة الأمريكية بالقاهرة', 'Cairo'],
      ['German University in Cairo', 'الجامعة الألمانية بالقاهرة', 'Cairo'],
      ['Alexandria University', 'جامعة الإسكندرية', 'Alexandria'],
      ['British University in Egypt', 'الجامعة البريطانية فى مصر', 'Cairo'],
      ['Ain Shams University', 'جامعة عين شمس', 'Cairo'],
      ['Future University in Egypt', 'جامعة المستقبل فى مصر', 'Cairo'],
      ['Nile University', 'جامعة النيل', 'Giza'],
      ['Mansoura University', 'جامعة المنصورة', 'Mansoura'],
      ['Canadian International College', 'الكلية الكندية الدولية', 'Cairo']
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { width: 30 }, // English Name
      { width: 30 }, // Arabic Name
      { width: 15 }  // City
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Universities');

    // Generate Excel file and download
    XLSX.writeFile(workbook, 'universities_template.xlsx');
    toast.success('Excel template downloaded successfully');
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'private':
        return 'bg-blue-100 text-blue-800';
      case 'international':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const UniversityForm = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name_en">English Name *</Label>
          <Input
            id="name_en"
            value={formData.name_en}
            onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
            placeholder="University name in English"
            required
          />
        </div>
        <div>
          <Label htmlFor="name_ar">Arabic Name *</Label>
          <Input
            id="name_ar"
            value={formData.name_ar}
            onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
            placeholder="اسم الجامعة بالعربية"
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="city">City *</Label>
        <Input
          id="city"
          value={formData.city}
          onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
          placeholder="City name"
          required
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            if (isEdit) {
              setShowEditDialog(false);
              setEditingUniversity(null);
            } else {
              setShowAddDialog(false);
            }
            resetForm();
          }}
        >
          Cancel
        </Button>
        <Button onClick={isEdit ? handleUpdate : handleAdd}>
          {isEdit ? 'Update' : 'Add'} University
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Bulk Import Universities</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Upload an Excel file or enter universities data manually
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                      <Download className="h-4 w-4 mr-2" />
                      CSV Template
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadExcelTemplate}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel Template
                    </Button>
                  </div>
                </div>

                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Instructions:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Option 1: Download the Excel template, fill it with your data, and upload it below</li>
                    <li>• Option 2: Copy data from your Excel sheet and paste it into the text area</li>
                    <li>• Format: English Name, Arabic Name, City</li>
                    <li>• Each university should be on a separate row/line</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="excel-upload" className="text-sm font-medium">
                      Upload Excel File
                    </Label>
                    <Input
                      id="excel-upload"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                      className="mt-1"
                    />
                    {selectedFile && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>

                  <div className="text-center text-sm text-muted-foreground">— OR —</div>

                  <div>
                    <Label htmlFor="bulk-text" className="text-sm font-medium">
                      Paste Universities Data
                    </Label>
                    <Textarea
                      id="bulk-text"
                      placeholder="Cairo University,جامعة القاهرة,Cairo&#10;Alexandria University,جامعة الإسكندرية,Alexandria"
                      value={bulkImportText}
                      onChange={(e) => setBulkImportText(e.target.value)}
                      rows={6}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBulkImportDialog(false);
                      setSelectedFile(null);
                      setBulkImportText('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleBulkImport}>
                    Import Universities
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {t('add_university', 'إضافة جامعة', 'Add University')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>
                  {t('add_new_university', 'إضافة جامعة جديدة', 'Add New University')}
                </DialogTitle>
              </DialogHeader>
              <UniversityForm />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge variant="secondary">{universities.length}</Badge>
            {t('universities', 'الجامعات', 'Universities')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('name', 'الاسم', 'Name')}</TableHead>
                  <TableHead>{t('city', 'المدينة', 'City')}</TableHead>
                  <TableHead>{t('type', 'النوع', 'Type')}</TableHead>
                  <TableHead>{t('created_at', 'تاريخ الإنشاء', 'Created At')}</TableHead>
                  <TableHead className="text-right">{t('actions', 'الإجراءات', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {universities.map((university) => (
                  <TableRow key={university.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {language === 'ar' ? university.name_ar : university.name_en}
                        </div>
                        {language === 'ar' && (
                          <div className="text-sm text-muted-foreground">{university.name_en}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{university.city}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeColor(university.type || 'public')}>
                        {university.type || 'public'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(university.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(university)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(university.id, university.name_en)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {universities.length === 0 && (
            <div className="text-center py-8">
              <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {t('no_universities', 'لا توجد جامعات', 'No universities found')}
              </p>
              <Button variant="outline" onClick={handleRefresh} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh List
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {t('edit_university', 'تعديل الجامعة', 'Edit University')}
            </DialogTitle>
          </DialogHeader>
          <UniversityForm isEdit />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UniversitiesManagement;
